import {
  assertReadingProgress, correctedMutationTime, estimateClockOffset,
  type Bookmark, type CloudBookmark, type CloudProgress, type CoverThumbnail, type ReaderState, type ReaderStateStore, type ReadingProgress, type StateRejection,
} from '@tanko/shared'
import { CloudApiClient } from '../lib/api-client'

type CloudReadingClient = Pick<CloudApiClient, 'readingState' | 'saveProgress' | 'saveBookmark' | 'deleteBookmark' | 'updateComic' | 'replaceCover'>

const toBookmark = (value: CloudBookmark): Bookmark => ({
  id: value.id, page: value.page, offset: value.offset, label: value.label,
  createdAt: value.createdAt, updatedAt: value.updatedAt,
})

export class CloudReadingStore implements ReaderStateStore {
  private clockCorrection = 0
  private rejected?: (rejection: StateRejection) => void
  private readonly progressMutations = new Map<string, CloudProgress>()
  private readonly bookmarkMutations = new Map<string, Omit<CloudBookmark, 'deleted'>>()
  private readonly deletionMutations = new Map<string, Pick<CloudBookmark, 'updatedAt' | 'mutationId'>>()

  constructor(
    private readonly comicId: string,
    private readonly pageCount: number,
    private readonly api: CloudReadingClient = new CloudApiClient(),
    private readonly clock: () => number = Date.now,
  ) {}

  onRejected(listener: (rejection: StateRejection) => void): void { this.rejected = listener }

  /** 合併冊一次載入全部成員狀態時沒有逐本呼叫 load，改由呼叫端把那一次請求的時鐘校正餵進來。 */
  calibrate(serverTime: number, startedAt: number, receivedAt: number): void {
    this.clockCorrection = estimateClockOffset(serverTime, startedAt, receivedAt)
  }

  async load(): Promise<ReaderState> {
    const startedAt = this.clock()
    const response = await this.api.readingState(this.comicId)
    const receivedAt = this.clock()
    this.clockCorrection = estimateClockOffset(response.serverTime, startedAt, receivedAt)
    return {
      progress: response.progress && {
        page: response.progress.page, offset: response.progress.offset, updatedAt: response.progress.updatedAt,
      },
      bookmarks: response.bookmarks.filter(bookmark => !bookmark.deleted).map(toBookmark),
      coverPage: response.coverPage,
    }
  }

  async saveProgress(progress: ReadingProgress): Promise<void> {
    assertReadingProgress(progress, this.pageCount)
    const key = `${progress.page}:${progress.offset}:${progress.updatedAt}`
    let mutation = this.progressMutations.get(key)
    if (!mutation) {
      mutation = { ...progress, updatedAt: correctedMutationTime(progress.updatedAt, this.clockCorrection), mutationId: crypto.randomUUID() }
      this.progressMutations.set(key, mutation)
    }
    const response = await this.api.saveProgress(this.comicId, mutation)
    this.progressMutations.delete(key)
    // accepted: false 代表輸掉 LWW 而非傳輸失敗，重送同一 mutation 結果相同，只能改採伺服器版本。
    if (!response.accepted) {
      this.rejected?.({ kind: 'progress', progress: { page: response.current.page, offset: response.current.offset, updatedAt: response.current.updatedAt } })
    }
  }

  async putBookmark(bookmark: Bookmark): Promise<void> {
    assertReadingProgress(bookmark, this.pageCount)
    const key = `${bookmark.id}:${bookmark.updatedAt}`
    let mutation = this.bookmarkMutations.get(key)
    if (!mutation) {
      const updatedAt = correctedMutationTime(bookmark.updatedAt, this.clockCorrection)
      mutation = { ...bookmark, createdAt: this.correctedCreatedAt(bookmark, updatedAt), updatedAt, mutationId: crypto.randomUUID() }
      this.bookmarkMutations.set(key, mutation)
    }
    const response = await this.api.saveBookmark(this.comicId, mutation)
    this.bookmarkMutations.delete(key)
    if (!response.accepted) this.rejectBookmark(response.current)
  }

  async deleteBookmark(id: string): Promise<void> {
    let mutation = this.deletionMutations.get(id)
    if (!mutation) {
      mutation = { updatedAt: correctedMutationTime(this.clock(), this.clockCorrection), mutationId: crypto.randomUUID() }
      this.deletionMutations.set(id, mutation)
    }
    const response = await this.api.deleteBookmark(this.comicId, id, mutation)
    this.deletionMutations.delete(id)
    if (!response.accepted) this.rejectBookmark(response.current)
  }

  /**
   * 雲端一本只有一張封面縮圖，換封面就是換那一張：縮圖與頁碼一起送，伺服器在同一個
   * batch 內更新。權威的封面頁碼以伺服器回應為準。
   */
  async setCover(page: number, thumbnail: CoverThumbnail): Promise<number> {
    if (!Number.isInteger(page) || page < 0 || page >= this.pageCount) throw new RangeError('封面頁碼超出範圍')
    if (!thumbnail.blob.size) throw new Error('雲端封面需要縮圖才能更新')
    const detail = await this.api.replaceCover(this.comicId, page, thumbnail.blob, thumbnail.width, thumbnail.height)
    return detail.coverPage
  }

  /** 還原預設就是把第一頁設為封面——同樣需要一張新縮圖，由呼叫端產生後經 setCover 送出。 */
  async clearCover(): Promise<number> {
    throw new Error('雲端作品還原封面請以第一頁的縮圖呼叫 setCover')
  }

  /**
   * createdAt 與 updatedAt 必須落在同一個時間基準。兩者同源於本機時鐘（新增書籤）時一起校正；
   * 沿用伺服器既有的 createdAt（更名）則保持原值，只夾在校正後的 updatedAt 以內。
   * 否則裝置時鐘快於伺服器時，只校正 updatedAt 會讓 createdAt > updatedAt 而被 API 以 422 拒絕。
   */
  private correctedCreatedAt(bookmark: Bookmark, correctedUpdatedAt: number): number {
    if (bookmark.createdAt >= bookmark.updatedAt) return correctedUpdatedAt
    return Math.min(bookmark.createdAt, correctedUpdatedAt)
  }

  private rejectBookmark(current: CloudBookmark): void {
    this.rejected?.({ kind: 'bookmark', bookmark: current.deleted ? null : toBookmark(current) })
  }
}
